import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import ButtonGroup from "@material-ui/core/ButtonGroup";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import { ChevronLeft, ChevronRight } from "@material-ui/icons";
import PrintIcon from "@material-ui/icons/Print";
import moment from "moment";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { BookingEvent, useBookingEvents } from "../../hooks/useBookingEvents";
import BookingSummary from "../../pages/chats/BookingSummary";
import { Color } from "../../types";
import { buildPrintFileName } from "../BookingCalendar";
import BookingCardLabel from "../BookingCard/BookingCardLabel";
import { getBookingCardStyle } from "../BookingCard/style";
import BookingStatusLegend from "../BookingStatusLegend";
import { StatusColorContext } from "../contexts";
import "../printChrome.css";
import {
  buildKanbanPrintRows,
  buildTimelinePrintRows,
  groupBookingsIntoBlocks,
  KANBAN_BLOCKS,
} from "./blocks";
import "./styles.css";

export type KanbanPrintLayout = "blocks" | "timeline";

/** Edit these to change the printed page header text. */
export const KANBAN_TITLE = "Booking Cases";

/** Footer counter for the printed pages; rendered by Chromium 131+ and Safari 18.2+. */
export const PAGE_NUMBER_RULE = `@page {
  @bottom-right {
    content: "Page " counter(page) " of " counter(pages);
    font-family: Roboto, Helvetica, Arial, sans-serif;
    font-size: 8pt;
    color: #444;
  }
}`;

const DATE_FORMAT = "YYYY-MM-DD";

interface KanbanCardProps {
  event: BookingEvent;
  colors: Color[];
  /** Omit to render a presentational copy — used by the print-only rows. */
  onSelect?: (event: BookingEvent) => void;
}

const KanbanCard: React.FC<KanbanCardProps> = ({ event, colors, onSelect }) => {
  const { style, className } = getBookingCardStyle(event.resource, colors);
  const content = (
    <div className="booking-kanban-card-content">
      <BookingCardLabel
        booking={event.resource}
        start={event.start}
        end={event.end}
        showTime
      />
    </div>
  );

  // The print rows duplicate every card, so keep that copy out of the
  // accessibility tree and off the tab order.
  if (!onSelect) {
    return (
      <div
        aria-hidden="true"
        className={`booking-kanban-card ${className}`.trim()}
        style={style}
      >
        {content}
      </div>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`Booking ${event.resource.id}`}
      className={`booking-kanban-card ${className}`.trim()}
      style={style}
      onClick={() => onSelect(event)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(event);
        }
      }}
    >
      {content}
    </div>
  );
};

interface Props {}

const BookingKanban: React.FC<Props> = (props) => {
  const [date, setDate] = useState<string>(() => moment().format(DATE_FORMAT));
  const { events, total } = useBookingEvents(date, date);
  const { colors } = useContext(StatusColorContext);
  const [selectedEvent, setSelectedEvent] = useState<BookingEvent>();
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [printMenuAnchor, setPrintMenuAnchor] =
    useState<null | HTMLElement>(null);
  const [printLayout, setPrintLayout] = useState<KanbanPrintLayout>("blocks");
  const [printedAt, setPrintedAt] = useState<string>(() =>
    moment().format("DD MMM YYYY, HH:mm"),
  );

  const blocks = useMemo(() => groupBookingsIntoBlocks(events), [events]);
  const printRows = useMemo(() => buildKanbanPrintRows(blocks), [blocks]);
  const timelinePrintRows = useMemo(
    () => buildTimelinePrintRows(events),
    [events],
  );

  // Mark the body while mounted so the print rules stay inert on other pages;
  // the @page rule declares no size — that would lock Chrome's print dialog
  // paper/orientation/scale controls. The bottom margin is wide enough to host
  // the page counter added below.
  useEffect(() => {
    const bodyClasses = ["booking-kanban-active", "booking-print-active"];
    const pageStyleId = "booking-kanban-print-page";
    document.body.classList.add(...bodyClasses);

    const pageStyle = document.createElement("style");
    pageStyle.id = pageStyleId;
    pageStyle.textContent = `@media print { @page { margin: 8mm 8mm 12mm; } }`;
    document.head.appendChild(pageStyle);

    // Page margin boxes are Chromium/Safari only. Adding the counter as its own
    // rule means engines that can't parse it (Firefox, jsdom) reject just this
    // one instead of discarding the margin above with it.
    const { sheet } = pageStyle;
    if (sheet) {
      try {
        sheet.insertRule(PAGE_NUMBER_RULE, sheet.cssRules.length);
      } catch {
        // No page numbers on this engine; everything else still prints.
      }
    }

    return () => {
      document.body.classList.remove(...bodyClasses);
      document.getElementById(pageStyleId)?.remove();
    };
  }, []);

  // Switches which print-only tbody and header rows reach paper.
  useEffect(() => {
    const layoutClass = `booking-kanban-print-layout-${printLayout}`;
    document.body.classList.add(layoutClass);
    return () => {
      document.body.classList.remove(layoutClass);
    };
  }, [printLayout]);

  // Refresh the printed-at time on every print (Ctrl+P, menu, window.print)
  useEffect(() => {
    const refreshPrintedAt = () =>
      setPrintedAt(moment().format("DD MMM YYYY, HH:mm"));
    window.addEventListener("beforeprint", refreshPrintedAt);
    return () => window.removeEventListener("beforeprint", refreshPrintedAt);
  }, []);

  // document.title is the suggested "Save as PDF" file name; restored on unmount.
  useEffect(() => {
    const previousTitle = document.title;
    const view =
      printLayout === "timeline" ? "kanban-timeline" : "kanban";
    document.title = buildPrintFileName(date, date, view);
    return () => {
      document.title = previousTitle;
    };
  }, [date, printLayout]);

  const shiftDay = (days: number) =>
    setDate(moment(date, DATE_FORMAT).add(days, "day").format(DATE_FORMAT));

  const handlePrevDay = () => shiftDay(-1);
  const handleNextDay = () => shiftDay(1);
  const handleToday = () => setDate(moment().format(DATE_FORMAT));

  const handlePrintMenuClose = () => setPrintMenuAnchor(null);

  const handlePrint = (layout: KanbanPrintLayout) => {
    handlePrintMenuClose();
    setPrintLayout(layout);
    // Two frames so the layout class lands before the print dialog opens.
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => window.print());
    });
  };

  // Clearing the native date input yields "", which would blank the board
  const handleDateChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (event.target.value) {
      setDate(event.target.value);
    }
  };

  const handleSelectEvent = (event: BookingEvent) => {
    setSelectedEvent(event);
    setIsDialogOpen(true);
  };

  const handleClose = () => setIsDialogOpen(false);

  const handleGoToBooking = () => {
    if (!selectedEvent) {
      return;
    }
    const win = window.open(
      `/#/bookings/${selectedEvent.resource.id}/show`,
      "_blank",
    );
    win?.focus();
  };

  return (
    <Box className="booking-kanban">
      {/* Browsers only repeat a real <thead> on every printed page, so the
          board lives in a table that renders as plain blocks on screen. */}
      <table className="booking-kanban-sheet">
        <thead>
          <tr className="booking-kanban-sheet-title-row">
            <th colSpan={KANBAN_BLOCKS.length}>
              <div className="booking-kanban-header">
                <div>
                  <h1>{KANBAN_TITLE}</h1>
                  <p>
                    {moment(date, DATE_FORMAT).format("dddd DD MMMM YYYY")}
                  </p>
                </div>
                <div className="booking-kanban-meta">
                  <span>Total: {total}</span>
                  <span className="booking-kanban-printed-at">
                    Printed: {printedAt}
                  </span>
                </div>
              </div>
            </th>
          </tr>
          {/* Print-only second header row — repeats the ranges above every page */}
          <tr className="booking-kanban-sheet-labels-row">
            {KANBAN_BLOCKS.map((block) => (
              <th key={block.label}>{block.label}</th>
            ))}
          </tr>
        </thead>
        <tbody className="booking-kanban-sheet-body--screen">
          <tr>
            <td colSpan={KANBAN_BLOCKS.length}>
              <div className="booking-kanban-nav">
                <ButtonGroup size="small" variant="outlined" disableElevation>
                  <Button onClick={handlePrevDay} startIcon={<ChevronLeft />}>
                    Back
                  </Button>
                  <Button onClick={handleToday}>Today</Button>
                  <Button onClick={handleNextDay} endIcon={<ChevronRight />}>
                    Next
                  </Button>
                </ButtonGroup>

                <TextField
                  id="booking-kanban-date"
                  label="Date"
                  type="date"
                  size="small"
                  variant="outlined"
                  value={date}
                  onChange={handleDateChange}
                  InputLabelProps={{ shrink: true }}
                />

                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<PrintIcon />}
                  endIcon={<ArrowDropDownIcon />}
                  onClick={(event) => setPrintMenuAnchor(event.currentTarget)}
                  aria-haspopup="true"
                  aria-controls={
                    printMenuAnchor ? "booking-kanban-print-menu" : undefined
                  }
                >
                  Print
                </Button>
                <Menu
                  id="booking-kanban-print-menu"
                  anchorEl={printMenuAnchor}
                  open={Boolean(printMenuAnchor)}
                  onClose={handlePrintMenuClose}
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                  transformOrigin={{ vertical: "top", horizontal: "left" }}
                >
                  <MenuItem onClick={() => handlePrint("blocks")}>
                    Print by time block
                  </MenuItem>
                  <MenuItem onClick={() => handlePrint("timeline")}>
                    Print by time
                  </MenuItem>
                </Menu>
              </div>

              <div className="booking-kanban-blocks">
                {KANBAN_BLOCKS.map((block, index) => (
                  <section className="booking-kanban-block" key={block.label}>
                    <h2 className="booking-kanban-block-range">
                      {block.label}
                    </h2>

                    <div className="booking-kanban-block-body">
                      {blocks[index].length === 0 && (
                        <p className="booking-kanban-block-empty">
                          No bookings
                        </p>
                      )}
                      {blocks[index].map((event) => (
                        <KanbanCard
                          key={event.resource.id}
                          event={event}
                          colors={colors}
                          onSelect={handleSelectEvent}
                        />
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            </td>
          </tr>
        </tbody>

        {/* Print-only mirror of the board: one row per card index so the three
            columns stay under their labels and break across pages together. */}
        <tbody className="booking-kanban-sheet-body--print">
          {printRows.length === 0 && (
            <tr>
              {KANBAN_BLOCKS.map((block) => (
                <td key={block.label}>
                  <p className="booking-kanban-block-empty">No bookings</p>
                </td>
              ))}
            </tr>
          )}
          {printRows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cards, columnIndex) => (
                <td key={KANBAN_BLOCKS[columnIndex].label}>
                  <div className="booking-kanban-print-cards">
                    {cards.map((event) => (
                      <KanbanCard
                        key={event.resource.id}
                        event={event}
                        colors={colors}
                      />
                    ))}
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>

        {/* Print-only chronological layout — one time-ordered row after another. */}
        <tbody className="booking-kanban-sheet-body--print-timeline">
          {timelinePrintRows.length === 0 && (
            <tr>
              <td colSpan={KANBAN_BLOCKS.length}>
                <p className="booking-kanban-block-empty">No bookings</p>
              </td>
            </tr>
          )}
          {timelinePrintRows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              <td colSpan={KANBAN_BLOCKS.length}>
                <div className="booking-kanban-print-cards booking-kanban-print-cards--timeline">
                  {row.map((event) => (
                    <KanbanCard
                      key={event.resource.id}
                      event={event}
                      colors={colors}
                    />
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {selectedEvent && (
        <Dialog
          open={isDialogOpen}
          onClose={handleClose}
          fullWidth={true}
          maxWidth={"md"}
        >
          <DialogTitle>{selectedEvent.title}</DialogTitle>
          <DialogContent>
            <BookingSummary id={selectedEvent.resource?.id} />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="secondary">
              Close
            </Button>
            <Button onClick={handleGoToBooking} color="primary">
              Go To Booking
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <BookingStatusLegend />
    </Box>
  );
};

export default BookingKanban;
